/**
 * Skill formula substitution helper — 2026-06-07.
 *
 * Produces the substituted-formula string a pregen sheet shows for each
 * skill (e.g. `[(14+12)/2] + 12 = 25` for Bond Boating) alongside the
 * abstract form (`[(PER+DEX)/2] + Skill Level`). Math mirrors
 * `engine/resolution.ts:computePrimaryChance` and the canonical Core
 * Rulebook p.31 SKILL FORMULAS.
 *
 * Lives in the engine layer (not the StatsPanel module) so it's testable
 * in isolation: vitest pins the substitution string for the canonical
 * pregens + asserts every (pregen, skill) row's stored primary_chance
 * matches the formula's computed result — a regression guard against any
 * future seed-data drift.
 */

import skillsCatalog from '@seed/skills.json';
import type { PCPregen } from '../types/pregen';

export type SkillCharacteristic = 'STR' | 'DEX' | 'WIL' | 'PER' | 'INT';

export type SkillFormulaPattern =
  | 'add_then_skill_level'
  | 'avg_then_add_skill_level'
  | 'wil_plus_charisma_skill_level_avg_then_add_skill_level';

export interface SkillFormulaSpec {
  form: string;
  characteristics: SkillCharacteristic[];
  pattern: SkillFormulaPattern;
}

interface SkillsCatalogEntry {
  name: string;
  formula?: string;
  characteristics?: string[];
  formula_pattern?: string;
}

interface SkillsCatalog {
  skills: SkillsCatalogEntry[];
}

const VALID_CHAR: ReadonlySet<string> = new Set(['STR', 'DEX', 'WIL', 'PER', 'INT']);
const VALID_PATTERN: ReadonlySet<string> = new Set([
  'add_then_skill_level',
  'avg_then_add_skill_level',
  'wil_plus_charisma_skill_level_avg_then_add_skill_level',
]);

/**
 * Skill-name aliases. 2026-06-07 NPC audit surfaced 7 (pregen, skill) rows
 * skipped from the consistency check because the seed JSONs spell the
 * skill as "Lockpicking and Safecracking" while the canonical
 * skills.json catalog uses "Lockpicking/Safecracking". Same skill; data
 * uses the prose form, catalog uses the slash form (matches Core
 * Rulebook p.31 SKILL FORMULAS table header). Mapping here is
 * minimally-invasive — no data renames + lookup still resolves.
 *
 * Engine code (engine/resolution.ts test fixture + chargen code) already
 * uses the slash form, so the canonical name stays as-is.
 */
const SKILL_NAME_ALIASES: ReadonlyMap<string, string> = new Map([
  ['Lockpicking and Safecracking', 'Lockpicking/Safecracking'],
]);

function canonicalSkillName(name: string): string {
  return SKILL_NAME_ALIASES.get(name) ?? name;
}

const _SPEC_BUILD: ReadonlyMap<string, SkillFormulaSpec> = new Map(
  (skillsCatalog as SkillsCatalog).skills
    .filter(
      (s): s is Required<Pick<SkillsCatalogEntry, 'name' | 'formula' | 'characteristics' | 'formula_pattern'>> & SkillsCatalogEntry =>
        typeof s.name === 'string' &&
        typeof s.formula === 'string' &&
        Array.isArray(s.characteristics) &&
        typeof s.formula_pattern === 'string' &&
        VALID_PATTERN.has(s.formula_pattern),
    )
    .map((s) => [
      s.name,
      {
        form: s.formula,
        characteristics: s.characteristics.filter(
          (c): c is SkillCharacteristic => VALID_CHAR.has(c),
        ),
        pattern: s.formula_pattern as SkillFormulaPattern,
      },
    ]),
);

export const SKILL_SPEC_BY_NAME: ReadonlyMap<string, SkillFormulaSpec> = _SPEC_BUILD;

export interface SubstitutedFormula {
  form: string;
  substituted: string;
  computed: number;
  matches: boolean;
}

/**
 * Returns null only when the skill is not catalogued in seed/skills.json
 * (e.g. an NPC-only skill on a pregen sheet).
 */
export function substituteSkillFormula(
  skillName: string,
  skillEntry: PCPregen['skills'][string],
  stats: PCPregen['stats'],
  charismaSkillLevel: number | null,
): SubstitutedFormula | null {
  const spec = SKILL_SPEC_BY_NAME.get(canonicalSkillName(skillName));
  if (!spec) return null;
  const sl = skillEntry.skill_level;
  const stored = skillEntry.primary_chance;
  let computed: number;
  let substituted: string;
  switch (spec.pattern) {
    case 'add_then_skill_level': {
      const c = spec.characteristics[0];
      if (!c) return null;
      const cv = stats[c];
      computed = cv + sl;
      substituted = `${cv} + ${sl} = ${computed}`;
      break;
    }
    case 'avg_then_add_skill_level': {
      const c1 = spec.characteristics[0];
      const c2 = spec.characteristics[1];
      if (!c1 || !c2) return null;
      const v1 = stats[c1];
      const v2 = stats[c2];
      const avg = Math.floor((v1 + v2) / 2);
      computed = avg + sl;
      substituted = `[(${v1}+${v2})/2] + ${sl} = ${computed}`;
      break;
    }
    case 'wil_plus_charisma_skill_level_avg_then_add_skill_level': {
      const wil = stats.WIL;
      const charismaSL = charismaSkillLevel ?? 0;
      const avg = Math.floor((wil + charismaSL) / 2);
      computed = avg + sl;
      substituted = `[(${wil} + Charisma SL ${charismaSL})/2] + ${sl} = ${computed}`;
      break;
    }
  }
  return {
    form: spec.form,
    substituted,
    computed,
    matches: computed === stored,
  };
}
